/**
 * ============================================================
 * ErrorBoundary — barrière d'erreur globale pour catho-paroisses
 * ============================================================
 *
 * Capture les exceptions React non gérées dans tout l'arbre enfant
 * et affiche un écran d'erreur amical au lieu d'un crash blanc.
 *
 * Les erreurs sont remontées à Sentry via captureException (no-op
 * si DSN absent).
 */

import React from 'react';
import { captureException } from '@/lib/sentry';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    captureException(error, {
      componentStack: info.componentStack ?? undefined,
    });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  handleReload = () => {
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    if (this.props.fallback) return this.props.fallback;

    return (
      <div
        role="alert"
        aria-live="assertive"
        className="flex min-h-screen items-center justify-center bg-background p-6"
      >
        <div className="max-w-md text-center">
          <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
            <svg
              className="h-8 w-8 text-destructive"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h1 className="mb-2 text-xl font-semibold text-foreground">
            Une erreur inattendue est survenue
          </h1>
          <p className="mb-6 text-sm text-muted-foreground">
            Pas de panique : votre travail est sauvegardé. Vous pouvez réessayer ou recharger l'application.
          </p>
          {import.meta.env.DEV && this.state.error && (
            <details className="mb-6 rounded-md border border-border bg-muted p-3 text-left text-xs text-muted-foreground">
              <summary className="cursor-pointer font-medium text-foreground">
                Détails techniques (dev only)
              </summary>
              <pre className="mt-2 overflow-x-auto whitespace-pre-wrap">
                {this.state.error.name}: {this.state.error.message}
                {this.state.error.stack ? `\n\n${this.state.error.stack}` : ''}
              </pre>
            </details>
          )}
          <div className="flex justify-center gap-3">
            <button
              type="button"
              onClick={this.handleReset}
              className="rounded-md border border-border bg-background px-4 py-2 text-sm font-medium text-foreground transition hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              Réessayer
            </button>
            <button
              type="button"
              onClick={this.handleReload}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition hover:bg-primary/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              Recharger
            </button>
          </div>
        </div>
      </div>
    );
  }
}
